import { Component, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { PlatformService } from './modules/shared/services/platform-service';
import { Store } from '@ngxs/store';
import { OidcSecurityService } from './modules/authenticate/auth-oidc-client/services/oidc.security.service';
import { CustomOIDC } from './modules/authenticate/oidc.auth.config';
import { WebsiteSd } from './modules/shared/services/seo/structured-data/website.sd';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SeoService } from './modules/shared/services/seo/seo.service';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { TranslateService } from '@ngx-translate/core';
import * as nlBE from '../assets/translations/nl-BE.json';
import { viewedSalesArticleKey, WebStorageService } from './modules/shared/services/web-storage/web-storage-service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from './modules/shared/components/header/header.component';
import { FooterComponent } from './modules/shared/components/footer/footer.component';
import { CategoryBannerComponent } from './modules/shared/components/category-banner/category-banner.component';
import { CookiePopupComponent } from './modules/shared/components/cookie-popup/cookie-popup.component';
import { JsonLdComponent } from './modules/shared/components/json-ld/json-ld.component';
import { LayoutState } from './state/layout/layout.state';
import { LoadCategories } from './state/layout/layout.actions';
import { DeviceDetectorService } from './modules/shared/services/device-detector/device-detector.service';
import { GetEmailConsent, Login, Logout } from '@app/state/account/account.actions';
import { AccountState } from '@app/state/account/account.state';
import { RefreshViewedSalesArticles, SetViewedSalesArticles } from '@app/state/sales-article/sales-article.actions';
import { filter } from 'rxjs';
import { UrlHelper } from '@env/url-helper';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    FontAwesomeModule,
    JsonLdComponent,
    HeaderComponent,
    FooterComponent,
    CategoryBannerComponent,
    CookiePopupComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  readonly platformService = inject(PlatformService);
  private readonly store = inject(Store);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly seoService = inject(SeoService);
  private readonly angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);
  private readonly translateService = inject(TranslateService);
  private readonly webStorageService = inject(WebStorageService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly urlHelper = inject(UrlHelper);

  readonly title = 'BoxDelivery';
  readonly showHeaderAndFooter$ = this.store.select(LayoutState.showHeaderAndFooter);
  readonly categories$ = this.store.select(LayoutState.categories).pipe(filter((val) => !!val?.length));
  readonly isMobile = signal(false);
  readonly structuredData: WebsiteSd;

  private authorizeEvent: (ev: CustomEvent) => void;

  constructor() {
    library.add(fas, fal, fab);
    this.structuredData = new WebsiteSd(
      this.urlHelper.apiUrl,
      this.urlHelper.frontendUrl + '/assets/images/fb-boxdelivery-logo.png'
    );
    this.authorizeEvent = this.authorizeHandler.bind(this);
  }

  ngOnInit() {
    this.translateService.setTranslation('nl', nlBE['default']);
    this.translateService.use('nl');

    this.angulartics2GoogleTagManager.startTracking();
    this.loadData();

    if (this.platformService.isBrowser()) {
      window.addEventListener('oidc-auth-success', this.authorizeEvent, false);

      this.oidcSecurityService.getIsAuthorized().subscribe((x) => {
        if (x) {
          this.store.dispatch(new Login());
        } else {
          this.store.dispatch(new Logout());
        }
      });

      const oidcConfig = new CustomOIDC(this.urlHelper.idpUrl, this.urlHelper.frontendUrl);
      this.oidcSecurityService.setupModule(oidcConfig.CustomisedOidcConfig, oidcConfig.CustomAuthWellKnownEndpoints);

      this.seoService.setOGSiteName(this.urlHelper.frontendUrl);
      this.isMobile.set(this.deviceService.isMobile());

      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.seoService.cleanUpMetaTags();
          this.seoService.setOGUrl(this.urlHelper.frontendUrl + event.url);
        }
      });

      window.addEventListener('click', (event) => {
        if (window['dataLayer'] == null) {
          window['dataLayer'] = [];
        }
        window['dataLayer'].push({
          'gtm.elementClasses': (<Element>event.target).classList,
          'gtm.elementId': (<Element>event.target).id,
          event: 'gtm.click'
        });
      });
    }
  }

  private loadData(): void {
    this.store.dispatch(new LoadCategories());
    this.store.dispatch(new RefreshViewedSalesArticles());

    if (this.store.selectSnapshot(AccountState.isAuthenticated)) {
      this.store.dispatch(new GetEmailConsent());
    }

    if (!!this.webStorageService.get(viewedSalesArticleKey)) {
      this.store.dispatch(new SetViewedSalesArticles(this.webStorageService.get(viewedSalesArticleKey)));
    }
  }

  private authorizeHandler(ev: CustomEvent) {
    this.oidcSecurityService.authorizedCallback(ev.detail);
  }
}
